import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiGlobe, FiCalendar, FiMapPin, FiCheck, FiInfo, FiSettings, FiClock, FiStar, FiX, FiAlertCircle, FiChevronLeft } from 'react-icons/fi';
import ReactCountryFlag from 'react-country-flag';
import { locationService } from '../services/locationService';
import { COUNTRIES } from './CountrySelector';
import { getPreferredCountry, setPreferredCountry, clearPreferredCountry } from '../utils/countryPreferences';
import { BaseModal } from './shared/BaseModal';
import { getBrowserFormatInfo, detectBrowserDateFormat } from '../utils/dateUtils';

const PreferencesHeader = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const PreferencesTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const PreferencesDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0;
  line-height: 1.5;
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;


const PreferencesSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LocationDetection = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${({ $isDetected }) => $isDetected ? 'rgba(16, 185, 129, 0.05)' : 'rgba(107, 114, 128, 0.05)'};
  border: 2px solid ${({ $isDetected }) => $isDetected ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.2)'};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: 16px;
  gap: 12px;
`;

const LocationIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isDetected }) => $isDetected ? '#10B981' : '#6B7280'};
  font-size: 20px;
`;

const LocationInfo = styled.div`
  flex: 1;
`;

const LocationText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: 500;
  margin-bottom: 4px;
`;

const LocationSubtext = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const DateFormatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
`;

const DateFormatOption = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background.card};
  border: 2px solid ${({ $isSelected, theme }) => $isSelected ? '#10B981' : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.shadows.sm};
  }
`;

const DateFormatInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DateFormatIcon = styled.div`
  font-size: 18px;
  color: ${({ $isSelected }) => $isSelected ? '#10B981' : '#6B7280'};
`;

const DateFormatDetails = styled.div`
  text-align: left;
`;

const DateFormatLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: 500;
`;

const DateFormatExample = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: 2px;
`;

const InfoNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-top: 16px;
`;

const InfoIcon = styled.div`
  color: #3B82F6;
  font-size: 16px;
  margin-top: 1px;
  flex-shrink: 0;
`;

const InfoText = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: 1.4;
`;

// Country Preference Styled Components
const CountryPreferenceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CountryPreferenceCard = styled.div`
  background: ${({ theme }) => theme.colors.background.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 60px;
`;

const CountryPreferenceFlag = styled.div`
  width: 32px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const CountryPreferenceDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CountryPreferenceName = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const CountryPreferenceStatus = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CountryPreferenceActions = styled.div`
  display: flex;
  gap: 8px;
`;

const CountryPreferenceButton = styled.button`
  background: ${({ $variant, theme }) =>
    $variant === 'primary' ? theme.colors.primary : 'transparent'};
  color: ${({ $variant, theme }) =>
    $variant === 'primary' ? 'white' : theme.colors.text.secondary};
  border: 1px solid ${({ $variant, theme }) =>
    $variant === 'primary' ? theme.colors.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 6px 12px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: ${({ $variant, theme }) =>
    $variant === 'primary' ? theme.colors.success : theme.colors.background.surface};
  }
`;

const CountrySelectorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 12px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background.surface};
`;

const CountrySelectorOption = styled.button`
  background: ${({ theme }) => theme.colors.background.card};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background.surface};
  }
`;

const CountrySelectorFlag = styled.div`
  width: 20px;
  height: 15px;
  border-radius: 2px;
  overflow: hidden;
  flex-shrink: 0;
`;

const CountrySelectorName = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 28px;
  flex-shrink: 0;
`;

const Button = styled(motion.button)`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${({ $primary, theme }) => $primary ? `
    background: ${theme.colors.gradient.primary};
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.prominent};
    }
  ` : `
    background: transparent;
    color: ${theme.colors.text.secondary};
    border: 1px solid ${theme.colors.border.default};
    
    &:hover {
      color: ${theme.colors.text.primary};
      border-color: ${theme.colors.primary};
    }
  `}
`;

// Get date format configurations with dynamic browser format
const getDateFormatConfigs = (browserFormatInfo) => {
  const autoFormat = browserFormatInfo?.datePattern || 'Browser Format';
  const autoExample = browserFormatInfo?.dateExample || 'Based on your system';
  
  return [
    {
      id: 'auto',
      label: 'Auto (Browser Default)',
      icon: '🌐',
      format: autoFormat,
      example: autoExample,
      description: 'Uses your browser\'s locale settings'
    },
    {
      id: 'us',
      label: 'United States',
      icon: '🇺🇸',
      format: 'MM/DD/YYYY',
      example: '09/21/2025'
    },
    {
      id: 'eu',
      label: 'European',
      icon: '🇪🇺',
      format: 'DD.MM.YYYY',
      example: '21.09.2025'
    },
    {
      id: 'uk',
      label: 'United Kingdom',
      icon: '🇬🇧',
      format: 'DD/MM/YYYY',
      example: '21/09/2025'
    }
  ];
};

// Get time format configurations with dynamic browser format
const getTimeFormatConfigs = (browserFormatInfo) => {
  const autoTimePattern = browserFormatInfo?.timePattern || 'Browser Format';
  const autoTimeExample = browserFormatInfo?.timeExample || 'Based on your system';
  
  // Create example times
  const testTime = new Date(2025, 8, 21, 15, 30); // 3:30 PM
  const locale = navigator.language || 'en-US';
  
  const time12h = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(testTime);
  
  const time24h = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(testTime);
  
  return [
    {
      id: 'auto',
      label: 'Auto (Browser Default)',
      icon: '🌐',
      format: autoTimePattern,
      example: autoTimeExample,
      description: 'Uses your browser\'s locale settings'
    },
    {
      id: '12h',
      label: '12-Hour (AM/PM)',
      icon: '🕐',
      format: '12h (AM/PM)',
      example: time12h,
      description: '12-hour format with AM/PM'
    },
    {
      id: '24h',
      label: '24-Hour',
      icon: '🕒',
      format: '24h',
      example: time24h,
      description: '24-hour format (00:00 to 23:59)'
    }
  ];
};

const UserPreferencesPopup = ({ isOpen, onClose, onConfirm }) => {
  // IP-based location detection
  const [ipLocation, setIpLocation] = useState('');
  const [userIP, setUserIP] = useState('');
  const [ipCountry, setIpCountry] = useState('');
  const [isIpDetected, setIsIpDetected] = useState(false);
  const [isIpLoading, setIsIpLoading] = useState(true);

  // Browser settings detection
  const [browserLocation, setBrowserLocation] = useState('');
  const [browserLocale, setBrowserLocale] = useState('');
  const [isBrowserDetected, setIsBrowserDetected] = useState(false);
  const [isBrowserLoading, setIsBrowserLoading] = useState(true);
  const [browserFormatInfo, setBrowserFormatInfo] = useState(null);

  const [selectedDateFormat, setSelectedDateFormat] = useState('auto');
  const [selectedTimeFormat, setSelectedTimeFormat] = useState('auto');

  // Country preference state
  const [preferredCountry, setPreferredCountryState] = useState(null);
  const [showCountrySelector, setShowCountrySelector] = useState(false);

  const detectIPLocation = useCallback(async () => {
    setIsIpLoading(true);

    try {
      // Use shared location service
      const locationData = await locationService.getLocationData();

      if (locationData && locationData.ip) {
        if (locationData.city && locationData.country) {
          setIpLocation(`${locationData.city}, ${locationData.country}`);
        } else {
          setIpLocation(locationData.country || 'Unknown');
        }

        setUserIP(locationData.ip);
        setIpCountry(locationData.country || 'Unknown');
        setIsIpDetected(true);
      } else {
        throw new Error('No location data available');
      }
    } catch (error) {
      console.log('Could not detect IP location:', error);
      setIpLocation('IP location not detected');
      setIsIpDetected(false);
    } finally {
      setIsIpLoading(false);
    }
  }, []);

  const detectBrowserSettings = useCallback(async () => {
    setIsBrowserLoading(true);

    try {
      // Get browser timezone and locale
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const userLocale = navigator.language || 'en-US';

      // Extract location from timezone
      const location = userTimezone.split('/').pop().replace(/_/g, ' ');
      const region = userTimezone.split('/')[0];

      setBrowserLocation(`${location} (${region})`);
      setBrowserLocale(userLocale);
      setIsBrowserDetected(true);

      // Get comprehensive browser format information
      const formatInfo = getBrowserFormatInfo();
      setBrowserFormatInfo(formatInfo);

      // Don't auto-select date format - preserve user's choice or default to 'auto'
      // The format will be determined by browser settings when 'auto' is selected

    } catch (error) {
      console.log('Could not detect browser settings:', error);
      setBrowserLocation('Browser settings not detected');
      setIsBrowserDetected(false);
    } finally {
      setIsBrowserLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Load saved preferences FIRST to preserve user's choice (especially 'auto')
      const savedPreferences = localStorage.getItem('userPreferences');
      if (savedPreferences) {
        try {
          const prefs = JSON.parse(savedPreferences);
          setSelectedDateFormat(prefs.dateFormat || 'auto');
          setSelectedTimeFormat(prefs.timeFormat || 'auto');
        } catch (error) {
          console.log('Error loading preferences:', error);
          // If loading fails, default to 'auto'
          setSelectedDateFormat('auto');
          setSelectedTimeFormat('auto');
        }
      } else {
        // No saved preferences - default to 'auto'
        setSelectedDateFormat('auto');
        setSelectedTimeFormat('auto');
      }

      // Load country preference
      const countryPref = getPreferredCountry();
      setPreferredCountryState(countryPref?.country || null);

      // Run both detections in parallel (after preferences are loaded)
      detectIPLocation();
      detectBrowserSettings();
    }
  }, [isOpen, detectIPLocation, detectBrowserSettings]);

  const handleDateFormatSelect = (formatId) => {
    setSelectedDateFormat(formatId);
  };

  const handleTimeFormatSelect = (formatId) => {
    setSelectedTimeFormat(formatId);
  };

  const handleCountryPreferenceSelect = (country) => {
    setPreferredCountryState(country);
    setPreferredCountry(country);
    setShowCountrySelector(false);
  };

  const handleClearCountryPreference = () => {
    setPreferredCountryState(null);
    clearPreferredCountry();
  };

  const handleToggleCountrySelector = () => {
    setShowCountrySelector(!showCountrySelector);
  };

  const handleConfirm = () => {
    const preferences = {
      dateFormat: selectedDateFormat,
      timeFormat: selectedTimeFormat,
      ipLocation,
      userIP,
      ipCountry,
      isIpDetected,
      browserLocation,
      browserLocale,
      isBrowserDetected,
      autoOpen: false,
      setupComplete: true,
      timestamp: Date.now()
    };

    localStorage.setItem('userPreferences', JSON.stringify(preferences));
    onConfirm(preferences);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      zIndex={1500}
      maxWidth="480px"
    >
        {/* Top Navigation */}
        <BackButton onClick={handleCancel}>
          <FiChevronLeft size={24} />
          Go Back
        </BackButton>

        <PreferencesHeader>
          <PreferencesTitle>
            <FiSettings />
            Setup Your Preferences
          </PreferencesTitle>
          <PreferencesDescription>
            Customize your experience for accurate visa calculations
          </PreferencesDescription>
        </PreferencesHeader>

        {/* IP Location Detection Section */}
        <PreferencesSection>
          <SectionTitle>
            <FiMapPin />
            IP Location Detection
          </SectionTitle>

          <LocationDetection $isDetected={isIpDetected}>
            <LocationIcon $isDetected={false}>
              <FiGlobe />
            </LocationIcon>
            <LocationInfo>
              <LocationText>
                {isIpLoading ? 'Detecting your IP location...' :
                  isIpDetected ? `Detected: ${ipLocation}` : 'IP location detection failed'}
              </LocationText>
              <LocationSubtext>
                {isIpLoading ? 'Please wait...' :
                  isIpDetected ? `IP: ${userIP} - Used for visa issuing country context` : 'May affect location accuracy'}
              </LocationSubtext>
            </LocationInfo>
            <FiAlertCircle style={{ color: '#3B82F6', fontSize: '18px', flexShrink: 0 }} />
          </LocationDetection>
        </PreferencesSection>

        {/* Browser Settings Detection Section */}
        <PreferencesSection>
          <SectionTitle>
            <FiClock />
            Browser Settings Detection
          </SectionTitle>

          <LocationDetection $isDetected={isBrowserDetected}>
            <LocationIcon $isDetected={false}>
              <FiGlobe />
            </LocationIcon>
            <LocationInfo>
              <LocationText>
                {isBrowserLoading ? 'Detecting browser settings...' :
                  isBrowserDetected ? `Detected: ${browserLocation}` : 'Browser detection failed'}
              </LocationText>
              <LocationSubtext>
                {isBrowserLoading ? 'Please wait...' :
                  isBrowserDetected ? (
                    browserFormatInfo ? (
                      <>
                        Locale: {browserLocale} • Date: {browserFormatInfo.datePattern} • Time: {browserFormatInfo.timePattern}
                        {selectedTimeFormat !== 'auto' && (
                          <span style={{ display: 'block', marginTop: '4px', fontWeight: 500 }}>
                            Selected: {selectedTimeFormat === '12h' ? '12h (AM/PM)' : '24h'}
                          </span>
                        )}
                      </>
                    ) : (
                      `Locale: ${browserLocale} - Used for date/time formatting`
                    )
                  ) : 'Using default formatting'}
              </LocationSubtext>
            </LocationInfo>
            <FiAlertCircle style={{ color: '#3B82F6', fontSize: '18px', flexShrink: 0 }} />
          </LocationDetection>
        </PreferencesSection>

        {/* Time Format Section */}
        <PreferencesSection>
          <SectionTitle>
            <FiClock />
            Time Format Preference
          </SectionTitle>

          <DateFormatGrid>
            {getTimeFormatConfigs(browserFormatInfo).map((format) => (
              <DateFormatOption
                key={format.id}
                $isSelected={selectedTimeFormat === format.id}
                onClick={() => handleTimeFormatSelect(format.id)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <DateFormatInfo>
                  <DateFormatIcon $isSelected={selectedTimeFormat === format.id}>
                    {format.icon}
                  </DateFormatIcon>
                  <DateFormatDetails>
                    <DateFormatLabel>{format.label}</DateFormatLabel>
                    <DateFormatExample>
                      {format.format} • {format.example}
                    </DateFormatExample>
                  </DateFormatDetails>
                </DateFormatInfo>
                {selectedTimeFormat === format.id ? (
                  <FiCheck style={{ color: '#10B981', fontSize: '18px' }} />
                ) : (
                  <FiSettings style={{ color: '#3B82F6', fontSize: '18px', opacity: 0.6 }} />
                )}
              </DateFormatOption>
            ))}
          </DateFormatGrid>

          <InfoNote>
            <InfoIcon>
              <FiInfo />
            </InfoIcon>
            <InfoText>
              "Auto" mode uses your browser's time format settings. Choose 12-hour (AM/PM) or 24-hour format to override.
            </InfoText>
          </InfoNote>
        </PreferencesSection>

        {/* Date Format Section */}
        <PreferencesSection>
          <SectionTitle>
            <FiCalendar />
            Date Format Preference
          </SectionTitle>

          <DateFormatGrid>
            {getDateFormatConfigs(browserFormatInfo).map((format) => (
              <DateFormatOption
                key={format.id}
                $isSelected={selectedDateFormat === format.id}
                onClick={() => handleDateFormatSelect(format.id)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <DateFormatInfo>
                  <DateFormatIcon $isSelected={selectedDateFormat === format.id}>
                    {format.icon}
                  </DateFormatIcon>
                  <DateFormatDetails>
                    <DateFormatLabel>{format.label}</DateFormatLabel>
                    <DateFormatExample>
                      {format.format} • {format.example}
                    </DateFormatExample>
                  </DateFormatDetails>
                </DateFormatInfo>
                {selectedDateFormat === format.id ? (
                  <FiCheck style={{ color: '#10B981', fontSize: '18px' }} />
                ) : (
                  <FiSettings style={{ color: '#3B82F6', fontSize: '18px', opacity: 0.6 }} />
                )}
              </DateFormatOption>
            ))}
          </DateFormatGrid>

          <InfoNote>
            <InfoIcon>
              <FiInfo />
            </InfoIcon>
            <InfoText>
              "Auto" mode uses your browser's locale settings for the most natural experience.
              Manual selection overrides browser defaults for consistent formatting.
            </InfoText>
          </InfoNote>
        </PreferencesSection>

        {/* Country Preference Section */}
        <PreferencesSection>
          <SectionTitle>
            <FiStar />
            Country Preference
          </SectionTitle>

          <CountryPreferenceContainer>
            {preferredCountry ? (
              <CountryPreferenceCard>
                <CountryPreferenceFlag>
                  <ReactCountryFlag
                    countryCode={preferredCountry.countryCode}
                    svg
                    style={{
                      width: '100%', height: '100%', objectFit: 'cover'
                    }}
                  />
                </CountryPreferenceFlag>
                <CountryPreferenceDetails>
                  <CountryPreferenceName>{preferredCountry.name}</CountryPreferenceName>
                  <CountryPreferenceStatus>
                    Preferred country - appears first in selection
                  </CountryPreferenceStatus>
                </CountryPreferenceDetails>
                <CountryPreferenceActions>
                  <CountryPreferenceButton
                    onClick={handleToggleCountrySelector}
                    $variant="primary"
                  >
                    Change
                  </CountryPreferenceButton>
                  <CountryPreferenceButton onClick={handleClearCountryPreference}>
                    <FiX size={14} />
                    Clear
                  </CountryPreferenceButton>
                </CountryPreferenceActions>
              </CountryPreferenceCard>
            ) : (
              <CountryPreferenceCard>
                <CountryPreferenceFlag>
                  <FiGlobe size={16} style={{ color: '#6B7280' }} />
                </CountryPreferenceFlag>
                <CountryPreferenceDetails>
                  <CountryPreferenceName>No preferred country set</CountryPreferenceName>
                  <CountryPreferenceStatus>
                    Set a preferred country for faster visa checks
                  </CountryPreferenceStatus>
                </CountryPreferenceDetails>
                <CountryPreferenceActions>
                  <CountryPreferenceButton
                    onClick={handleToggleCountrySelector}
                    $variant="primary"
                  >
                    Set Preference
                  </CountryPreferenceButton>
                </CountryPreferenceActions>
              </CountryPreferenceCard>
            )}

            {showCountrySelector && (
              <CountrySelectorGrid>
                {COUNTRIES.map((country) => (
                  <CountrySelectorOption
                    key={country.code}
                    onClick={() => handleCountryPreferenceSelect(country)}
                  >
                    <CountrySelectorFlag>
                      <ReactCountryFlag
                        countryCode={country.countryCode}
                        svg
                        style={{
                          width: '100%', height: '100%', objectFit: 'cover'
                        }}
                      />
                    </CountrySelectorFlag>
                    <CountrySelectorName>{country.name}</CountrySelectorName>
                  </CountrySelectorOption>
                ))}
              </CountrySelectorGrid>
            )}
          </CountryPreferenceContainer>

          <InfoNote>
            <InfoIcon>
              <FiInfo />
            </InfoIcon>
            <InfoText>
              Your preferred country will appear first when selecting a visa issuing country,
              making frequent checks faster and more convenient.
            </InfoText>
          </InfoNote>
        </PreferencesSection>

        <ButtonGroup>
          <Button
            onClick={handleCancel}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Skip Setup
          </Button>
          <Button
            $primary
            onClick={handleConfirm}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Save Preferences
          </Button>
        </ButtonGroup>
    </BaseModal>
  );
};

export default UserPreferencesPopup;